import { Controller } from "@hotwired/stimulus";
import { WebAuthnSigner } from "@dfns/sdk-browser";
import toast from "../utils/toast";
import { getUserFriendlyErrorMessage } from "../utils/errors";
import { handleBackendResponse } from "../utils/backendResponseHandler";
import { rrFetch } from "../utils/rr_fetch";

// cash-passkey: passkey approval for a withdraw-to-cash order. Verify-only — it
// requests a no-op DFNS challenge, signs it with WebAuthn to prove passkey
// possession, and submits. The backend then reserves the balance and creates the
// PENDING order (no money moves). Mirrors removePasskeyController's challenge flow.
export default class CashPasskeyController extends Controller {
  static values = {
    domain: String,
    company: { type: String, default: "CoinSub Wallet" },
    csrfToken: String,
    idempotencyKey: String,
    payload: Object,
  };

  static targets = ["confirmButton", "spinner"];

  connect() {
    this.isProcessing = false;
  }

  async approve(event) {
    event.preventDefault();
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.showSpinner();

    try {
      const { challenge } = await this.requestChallenge();
      const signedChallenge = await this.signChallenge(challenge);
      await this.submitApproval(signedChallenge);
      // On success the backend rendered the "ready" Turbo Stream; nothing else to do.
    } catch (error) {
      if (error.message !== "backend-error") {
        if (error.name === "TypeError" || error.message?.includes("fetch")) {
          toast("Unable to connect to server.", "error");
        } else {
          toast(getUserFriendlyErrorMessage(error), "error");
        }
      }
      this.isProcessing = false;
      this.hideSpinner();
    }
  }

  async requestChallenge() {
    const response = await rrFetch("/withdraw/cash/passkey/request", {
      method: "POST",
      headers: { Accept: "application/json", "X-CSRF-Token": this.csrfTokenValue },
      credentials: "same-origin",
    });
    return handleBackendResponse(response, "Failed to start passkey approval.");
  }

  async signChallenge(challengeData) {
    const relyingPartyId = this.domainValue || window.location.hostname;
    const webauthn = new WebAuthnSigner({
      relyingParty: { id: relyingPartyId, name: this.companyValue },
    });

    try {
      const assertion = await webauthn.sign(challengeData);
      const credentialAssertion = assertion.credentialAssertion || {};
      return {
        challengeIdentifier: challengeData.challengeIdentifier,
        signature: {
          kind: assertion.kind,
          credentialAssertion: {
            credId: credentialAssertion.credId || "",
            clientData: credentialAssertion.clientData || "",
            signature: credentialAssertion.signature || "",
            authenticatorData: credentialAssertion.authenticatorData || "",
            userHandle: credentialAssertion.userHandle || "",
          },
        },
      };
    } catch (error) {
      if (error.name === "InvalidStateError" || error.message?.includes("pending")) {
        toast("A passkey request is already in progress. Please refresh the page and try again.", "error");
        throw new Error("Passkey request already pending");
      }
      throw error;
    }
  }

  async submitApproval({ challengeIdentifier, signature }) {
    const response = await rrFetch("/withdraw/cash/passkey/submit", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfTokenValue },
      body: JSON.stringify({
        challengeIdentifier,
        signature,
        payload: this.payloadValue,
        idempotencyKey: this.idempotencyKeyValue,
      }),
      credentials: "same-origin",
    });
    await handleBackendResponse(response, "Failed to approve cash order.");
  }

  showSpinner() {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("hidden");
    if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = true;
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("hidden");
    if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = false;
  }
}
